import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import { catchError, map, delay } from 'rxjs/operators';
import { environment } from '../environments/environment';

export interface PresignedUrlResponse {
  payload: {
    url: string;
    key: string;
    viewUrl: string;
  };
}

export interface UploadedImage {
  name: string;
  size: number;
  objectURL: string;
  s3Key: string;
  uploading: boolean;
  uploaded: boolean;
}

export interface ScanRequest {
  imageKey: string;
  imageUrl: string;
  latitude?: number;
  longitude?: number;
  accuracy?: number;
  timestamp: string;
  metadata?: Record<string, any>;
}

export interface ScanResponse {
  status: string;
  payload?: {
    success?: boolean;
    redirectUrl?: string;
    [key: string]: any;
  };
  message?: string;
  error?: string | null;
  imageKey?: string;
  imageUrl?: string;
  // Legacy fields for backward compatibility
  success?: boolean;
  data?: any;
  scanId?: string;
  confidence?: number;
  tags?: string[];
  metadata?: Record<string, any>;
}

@Injectable({
  providedIn: 'root',
})
export class ImageUploadService {
  private readonly apiUrl = environment.apiUrl || '/api'; // Use environment API URL
  private readonly laavaScanUrl = (environment as any).laavaScanUrl || 'https://api.laava.id/scan';
  private useMock = false; // Set to false when backend is ready

  constructor(private readonly http: HttpClient) { }

  /**
   * Get presigned URL for S3 upload
   */
  getPresignedUrl(
    fileName: string,
    fileType: string,
    uploadType: string = 'camera-scan',
    tenantId?: string
  ): Observable<PresignedUrlResponse> {
    if (this.useMock) {
      // Simulate API delay
      return of({
        payload: {
          url: 'https://mock-s3-upload-url.com/upload',
          key: `camera-scan/${Date.now()}_${fileName}`,
          viewUrl: `https://mock-s3-bucket.com/camera-scan/${Date.now()}_${fileName}`
        }
      }).pipe(delay(1000)); // Simulate network delay
    }

    const params = new HttpParams()
      .set('fileName', fileName)
      .set('fileType', fileType)
      .set('type', uploadType);

    const url = `${this.apiUrl}/api/public-scan/presigned-upload-url`;

    // Add tenant ID header if provided
    const headers: { [key: string]: string } = {};
    if (tenantId) {
      headers['x-tenant-id'] = tenantId;
      console.log('Adding x-tenant-id header for presigned URL:', tenantId);
    } else {
      console.log('No tenant ID provided for presigned URL request');
    }

    return this.http
      .get<PresignedUrlResponse>(url, {
        params,
        headers
      })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Upload file to S3 using presigned URL
   */
  uploadFileToS3(url: string, file: File): Observable<any> {
    if (this.useMock) {
      // Simulate S3 upload success response
      return of({
        status: 200,
        statusText: 'OK',
        headers: {},
        body: null
      }).pipe(delay(2000));
    }

    return this.http.put(url, file, {
      headers: { 'Content-Type': file.type },
      observe: 'response',
    });
  }

  /**
   * Complete S3 upload and send to Laava API via server
   */
  uploadImageAndScan(
    file: File,
    location?: { latitude: number; longitude: number; accuracy: number },
    _tenantId?: string
  ): Observable<ScanResponse> {
    const fileName = file.name;

    return this.scanImageDirectWithLaava(file, fileName).pipe(
      map(laavaData => ({
        status: 'success',
        payload: {
          laavaData,
        },
        message: 'Direct Laava scan completed',
        error: null,
        imageKey: `direct_${Date.now()}`,
        imageUrl: undefined,
        location,
      } as ScanResponse)),
      catchError(this.handleError)
    );
  }

  /**
   * Direct scan: POST multipart/form-data { image: <file> } to Laava
   */
  private scanImageDirectWithLaava(image: Blob, fileName: string): Observable<any> {
    if (this.useMock) {
      return of({
        message: 'Mock Laava response',
        data: {
          custom_attributes: {
            url_positive: 'https://example.com/positive'
          }
        }
      }).pipe(delay(1000));
    }

    const form = new FormData();
    form.append('image', image, fileName);

    return this.http.post(this.laavaScanUrl, form, { responseType: 'text' }).pipe(
      map((raw: string) => {
        try {
          return JSON.parse(raw);
        } catch {
          return raw;
        }
      }),
      catchError(error => {
        return throwError(() => new Error('Direct Laava scan failed: ' + (error?.message || 'Unknown error')));
      })
    );
  }

  /**
   * Send scan request to server (server will call scanning service)
   */
  private sendScanRequest(request: ScanRequest, tenantId?: string): Observable<ScanResponse> {
    if (this.useMock) {
      // Simulate successful scan response matching backend structure
      return of({
        status: 'success',
        payload: {
          success: true,
          redirectUrl: 'https://www.google.com'
        },
        message: 'Scan processed successfully',
        error: null,
        // Legacy fields for backward compatibility
        success: true,
        scanId: `scan_${Date.now()}`,
        confidence: 0.95,
        tags: ['product', 'barcode', 'scanned'],
        data: {
          detectedText: 'Sample Product 123',
          barcode: '1234567890123',
          confidence: 0.95
        }
      }).pipe(delay(1500)); // Simulate processing delay
    }

    const url = `${this.apiUrl}/api/public-scan/scan`;

    // Add tenant ID header if provided
    const headers: { [key: string]: string } = {};
    if (tenantId) {
      headers['x-tenant-id'] = tenantId;
      console.log('Adding x-tenant-id header for scan request:', tenantId);
    } else {
      console.log('No tenant ID provided for scan request');
    }

    return this.http
      .post<ScanResponse>(url, request, { headers })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Get scan status from server
   */
  getScanStatus(scanId: string): Observable<ScanResponse> {
    if (this.useMock) {
      return of({
        success: true,
        scanId,
        status: 'completed'
      });
    }

    return this.http
      .get<ScanResponse>(`${this.apiUrl}/public-scan/scan/status/${scanId}`)
      .pipe(
        map(response => response),
        catchError(this.handleError)
      );
  }

  /**
   * Get scan history from server
   */
  getScanHistory(limit: number = 50, offset: number = 0): Observable<ScanResponse[]> {
    if (this.useMock) {
      return of([]);
    }

    const params = new HttpParams()
      .set('limit', limit.toString())
      .set('offset', offset.toString());

    return this.http
      .get<{ history: ScanResponse[] }>(`${this.apiUrl}/public-scan/scan/history`, {
        params,
      })
      .pipe(
        map(response => response.history || []),
        catchError(this.handleError)
      );
  }

  /**
   * Delete scan record
   */
  deleteScan(scanId: string): Observable<{ success: boolean }> {
    if (this.useMock) {
      return of({ success: true });
    }

    return this.http
      .delete<{ success: boolean }>(`${this.apiUrl}/public-scan/scan/${scanId}`)
      .pipe(
        map(response => response),
        catchError(this.handleError)
      );
  }

  /**
   * Handle HTTP errors
   */
  private handleError = (error: any): Observable<never> => {
    let errorMessage = 'An unexpected error occurred';

    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = error.error.message;
    } else if (error.status) {
      // Server-side error
      switch (error.status) {
        case 400:
          errorMessage = 'Bad request: Invalid data provided';
          break;
        case 401:
          errorMessage = 'Unauthorized: Please log in again';
          break;
        case 403:
          errorMessage = 'Forbidden: Insufficient permissions';
          break;
        case 404:
          errorMessage = 'Service not found';
          break;
        case 413:
          errorMessage = 'Image file too large';
          break;
        case 429:
          errorMessage = 'Rate limit exceeded. Please try again later';
          break;
        case 500:
          errorMessage = 'Internal server error. Please try again later';
          break;
        case 503:
          errorMessage = 'Service temporarily unavailable';
          break;
        default:
          errorMessage = `Server error: ${error.status}`;
      }

      if (error.error?.message) {
        errorMessage += ` - ${error.error.message}`;
      }
    }

    return throwError(() => new Error(errorMessage));
  };

  /**
   * Create uploaded image object for state management
   */
  createUploadedImage(
    file: File,
    s3Key: string,
    viewUrl: string
  ): UploadedImage {
    return {
      name: file.name,
      size: file.size,
      objectURL: viewUrl,
      s3Key: s3Key,
      uploading: false,
      uploaded: true,
    };
  }

  /**
   * Validate file before upload
   */
  validateFile(file: File): { valid: boolean; error?: string } {
    // Check file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      return {
        valid: false,
        error: 'File size must be less than 10MB',
      };
    }

    // Check file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      return {
        valid: false,
        error: 'Only JPEG, PNG, and WebP images are allowed',
      };
    }

    return { valid: true };
  }

  /**
   * Get current mock mode status
   */
  isMockModeEnabled(): boolean {
    return this.useMock;
  }
} 